import {
  DEFAULT_CANVAS_FONT_FAMILY,
  DEFAULT_CANVAS_FONT_STYLE,
  DEFAULT_CANVAS_FONT_WEIGHT,
} from "../canvasFonts";
import {
  TRANSPARENT_CANVAS_FILL,
  duplicateCanvasElement,
  moveCanvasElement,
  updateElementStyle,
} from "../nativeCanvasElements";

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

const DEFAULT_SELECTION_STYLE = Object.freeze({
  fill: "#111111",
  fontFamily: DEFAULT_CANVAS_FONT_FAMILY,
  fontSize: 20,
  fontStyle: DEFAULT_CANVAS_FONT_STYLE,
  fontWeight: DEFAULT_CANVAS_FONT_WEIGHT,
  stroke: "#ff9f43",
  strokeWidth: 2,
});

/**
 * @param {{ delta: { x: number, y: number }, elements: Array<object>, ids: Set<string> }} params
 * @returns {Array<object>}
 */
export const moveElementsBy = ({ delta, elements, ids }) =>
  elements.map((element) => {
    if (!ids.has(element.id)) return element;
    return moveCanvasElement({ delta, element });
  });

/**
 * @param {{ elements: Array<object>, ids: Set<string> }} params
 * @returns {{ elements: Array<object>, newIds: Array<string> }}
 */
export const duplicateElements = ({ elements, ids }) => {
  const duplicates = elements
    .filter((element) => ids.has(element.id))
    .map(duplicateCanvasElement);

  return {
    elements: [...elements, ...duplicates],
    newIds: duplicates.map((element) => element.id),
  };
};

/**
 * @param {{ elements: Array<object>, ids: Set<string> }} params
 * @returns {Array<object>}
 */
export const deleteElements = ({ elements, ids }) =>
  elements.filter((element) => !ids.has(element.id));

/**
 * @param {{ elements: Array<object>, ids: Set<string>, style: object }} params
 * @returns {Array<object>}
 */
export const applyStyleToElements = ({ elements, ids, style }) =>
  elements.map((element) => {
    if (!ids.has(element.id)) return element;
    return updateElementStyle({ element, style });
  });

const normalizeColorValue = ({ fallback, value }) =>
  typeof value === "string" && HEX_COLOR_PATTERN.test(value) ? value : fallback;

// Fill additionally admits the no-fill sentinel so the style bar can reflect
// and toggle transparent shapes; stroke stays hex-only.
const normalizeFillValue = ({ fallback, value }) =>
  value === TRANSPARENT_CANVAS_FILL
    ? value
    : normalizeColorValue({ fallback, value });

/**
 * Style shown in the style bar for the current selection (first selected
 * element wins, like Excalidraw).
 * @param {{ elements: Array<object>, ids: Set<string> }} params
 * @returns {{ fill: string, fontFamily: string, fontSize: number, fontStyle: string, fontWeight: number, stroke: string, strokeWidth: number }}
 */
export const getSelectionStyle = ({ elements, ids }) => {
  const selectedElement = elements.find((element) => ids.has(element.id));
  const style = selectedElement?.style ?? {};

  return {
    fill: normalizeFillValue({
      fallback: DEFAULT_SELECTION_STYLE.fill,
      value: style.fill,
    }),
    fontFamily: style.fontFamily ?? DEFAULT_SELECTION_STYLE.fontFamily,
    fontSize: style.fontSize ?? DEFAULT_SELECTION_STYLE.fontSize,
    fontStyle: style.fontStyle ?? DEFAULT_SELECTION_STYLE.fontStyle,
    fontWeight: style.fontWeight ?? DEFAULT_SELECTION_STYLE.fontWeight,
    stroke: normalizeColorValue({
      fallback: DEFAULT_SELECTION_STYLE.stroke,
      value: style.stroke,
    }),
    strokeWidth: style.strokeWidth ?? DEFAULT_SELECTION_STYLE.strokeWidth,
  };
};
